import type { CageType as CageTypeRich } from '@/domain/cages/types';
import {
    CAGE_TYPE_NAMES,
    FISH_TYPE_NAMES,
    LICE_TYPE_SHORT_NAMES,
    LOCATION_TYPE_SHORT_NAMES,
} from '@/domain/cages/types';

/**
 * A location type or lice type, in any of the shapes the app carries them around as:
 *   - A short name string — the `LocationType` enum from the Client Overview endpoint
 *   - An object shape `{ shortName?: string }` used by the cages and auth domains, including the
 *     cage context's `location.locationType` and `stock.liceType` blocks
 *   - `null`/`undefined` when the type is not yet loaded
 */
type ShortNameLike = string | { shortName?: string } | null | undefined;

function shortNameOf(value: ShortNameLike): string | undefined {
    if (value == null) return undefined;
    if (typeof value === 'string') return value;
    return value.shortName;
}

export function isSmoltLocation(locationType?: ShortNameLike): boolean {
    return shortNameOf(locationType) === LOCATION_TYPE_SHORT_NAMES.SMOLT;
}

export function isLandbasedLocation(locationType?: ShortNameLike): boolean {
    return shortNameOf(locationType) === LOCATION_TYPE_SHORT_NAMES.LANDBASED;
}

export function isDefaultLocation(locationType?: ShortNameLike): boolean {
    const shortName = shortNameOf(locationType);
    return !shortName || shortName === LOCATION_TYPE_SHORT_NAMES.DEFAULT;
}

/**
 * Chilean sea lice, which the UI counts differently (Caligus instead of the Norwegian variants).
 *
 * Takes the lice type in any of its shapes: the cage context's `stock.liceType` block, the
 * domain/auth `{ shortName }` object, or the client-overview `locationLiceType` enum.
 */
export function isChileanLiceType(liceType?: ShortNameLike): boolean {
    return shortNameOf(liceType) === LICE_TYPE_SHORT_NAMES.CHILEAN;
}

export function isSmoltProductionTank(cageType?: { name?: string }): boolean {
    return cageType?.name === CAGE_TYPE_NAMES.SMOLT_PRODUCTION;
}

export function isLandbasedTank(cageType?: { name?: string }): boolean {
    return cageType?.name === CAGE_TYPE_NAMES.LANDBASED;
}

/**
 * Matches on `fishType.name` (the DB name, not `shortName`).
 *
 * The species passed in is the pen's, since a location can hold several (WB-2207); the context
 * resolves it with the location as a fallback. Accepts a minimal shape on purpose, so both the
 * context's `FishTypeBlock` and the richer `DomainFishType` can be passed in.
 *
 * This answers identity, not behaviour. Do not build species-conditional behaviour on it: a
 * comparison here hands every species added later whatever trout is not. Behaviour belongs in
 * `domain/cages/speciesTraits`, where an unlisted species takes the salmon default.
 */
export function isTroutFishType(fishType?: { name?: string }): boolean {
    return fishType?.name === FISH_TYPE_NAMES.TROUT;
}

/**
 * Returns the subset of cage types allowed for a given location type.
 *
 * - smolt_location (Location type)     → smolt_production only (locked, single option)
 * - landbased (Location type)          → landbased + smolt_production (selectable)
 * - default / null (Location type)     → all except smolt_production and landbased
 */
export function allowedCageTypes(
    cageTypesList: CageTypeRich[],
    locationType?: ShortNameLike
): CageTypeRich[] {
    if (isSmoltLocation(locationType)) {
        return cageTypesList.filter((c) => c.name === CAGE_TYPE_NAMES.SMOLT_PRODUCTION);
    }
    if (isLandbasedLocation(locationType)) {
        return cageTypesList.filter(
            (c) =>
                c.name === CAGE_TYPE_NAMES.LANDBASED || c.name === CAGE_TYPE_NAMES.SMOLT_PRODUCTION
        );
    }
    return cageTypesList.filter(
        (c) => c.name !== CAGE_TYPE_NAMES.SMOLT_PRODUCTION && c.name !== CAGE_TYPE_NAMES.LANDBASED
    );
}
